import { css } from '@emotion/react';
import { t } from 'i18n';
import type { Option } from 'models';
import { useState } from 'react';
import type { CodeName, BonusProgram } from 'shared';

import { CheckboxField } from '../../forms/ui/Checkbox';
import { theme } from '../../theme';

import { FrequentFlyerModal } from './FrequentFlyerModal';
import { buttonOverride } from './styles';
import { WarningModal } from './WarningModal';

const { colors, spacing, fontWeight } = theme;

const styles = {
  programContainer: css`
    display: flex;
    gap: ${spacing.xs};
    margin-right: ${spacing.sm};
  `,
  titleContainer: css`
    display: flex;
  `,
  programWrapper: css`
    display: flex;
    flex-direction: column;
  `,
  selectedProgramWrapper: css`
    display: flex;
  `,
  checkboxContainer: css`
    margin-top: ${spacing.sm};
    display: flex;
    align-items: center;
  `,
  linkContainer: css`
    display: flex;
    gap: ${spacing.md};
  `,
  fontWeightBlack: css`
    font-weight: ${fontWeight.black};
  `,
  error: css`
    color: ${colors.danger};
    margin-top: ${spacing.xs};
  `,
};

type Props = {
  bonusProgram: BonusProgram | undefined;
  options: Option[] | undefined;
  origin?: CodeName;
  destination?: CodeName;
  error: string | undefined;
  onChange: (bonusProgram: BonusProgram | undefined) => void;
  displayCheckbox?: boolean;
};

export const BonusProgramComponent = ({
  bonusProgram,
  options,
  origin,
  destination,
  error,
  onChange,
  displayCheckbox = true,
}: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const onSave = (bonusProgram: BonusProgram) => {
    onChange(bonusProgram);
    setShowModal(false);
  };
  const onRemove = () => {
    onChange(undefined);
    setShowWarningModal(false);
  };

  if (!origin && !destination) return null;

  const selectedProgramName = options?.find(
    bonusProgramOptions => bonusProgramOptions.value === bonusProgram?.bonusProgramKey
  );

  return (
    <>
      <div css={styles.programWrapper}>
        {bonusProgram && <h4>{t('travelers.ffpTitle')}</h4>}
        <div css={styles.selectedProgramWrapper}>
          {bonusProgram && (
            <div css={styles.programContainer}>
              <div key={bonusProgram.bonusProgramKey}>
                <span>{selectedProgramName?.label}</span>{' '}
                <span css={styles.fontWeightBlack}>{bonusProgram.loyaltyNumber}</span>
              </div>
            </div>
          )}
          <div css={styles.linkContainer}>
            <button css={buttonOverride} onClick={() => setShowModal(true)}>
              {bonusProgram ? t('common:edit') : t('travelers.addBonusProgram')}
            </button>
            {bonusProgram && (
              <button css={buttonOverride} onClick={() => setShowWarningModal(true)}>
                {t('common:remove')}
              </button>
            )}
          </div>
        </div>
      </div>

      {!!error && bonusProgram && <p css={styles.error}>{error}</p>}

      {bonusProgram && displayCheckbox && (
        <div css={styles.checkboxContainer}>
          <CheckboxField
            checked={!bonusProgram.earnPoints}
            onChange={() => {
              onChange({ ...bonusProgram, earnPoints: !bonusProgram.earnPoints });
            }}
          />
          <span>{t('travelers.preventBenefits')}</span>
        </div>
      )}
      {showModal && (
        <FrequentFlyerModal
          selected={bonusProgram}
          options={options ?? []}
          onSave={onSave}
          onClose={() => setShowModal(false)}
          origin={origin}
          destination={destination}
        />
      )}
      {showWarningModal && <WarningModal onClose={() => setShowWarningModal(false)} onRemove={onRemove} />}
    </>
  );
};
